import { useState, useEffect } from 'react';
import { HelpTip, Loading, PANEL_HELP, api, useToast, PanelHowTo } from './_barrel.js';


        function HostileMentionPanel({ token, subjectId, onIncidentsChanged, selectedAccount, onClearAccount }) {
            const [checking, setChecking] = useState(false);
            const [checkingBsky, setCheckingBsky] = useState(false);
            const [checkingX, setCheckingX] = useState(false);
            const [reclassifying, setReclassifying] = useState(false);
            const [runningAll, setRunningAll] = useState(false);
            const [runAllStatus, setRunAllStatus] = useState('');
            const [advancedOpen, setAdvancedOpen] = useState(false);
            const [lastResult, setLastResult] = useState(null);
            const [history, setHistory] = useState(null);
            const [error, setError] = useState(null);
            const [expanded, setExpanded] = useState(false);
            const [filter, setFilter] = useState('hostile_first');  // 'hostile_first' | 'hostile_only' | 'all'
            const toast = useToast();

            const reloadHistory = async () => {
                try {
                    const rows = await api(`/api/v1/subjects/${subjectId}/online-mentions?limit=200`, {}, token);
                    setHistory(rows);
                } catch (e) { setError(e.message); }
            };

            useEffect(() => { reloadHistory(); }, [token, subjectId]);

            const runCheck = async () => {
                setChecking(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/online-mentions/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult({ ...result, source: 'Google News' });
                    await reloadHistory();
                    if (result.new_incidents_created > 0) {
                        toast(`Google News: ${result.new_incidents_created} new mention(s) added`);
                        onIncidentsChanged?.();
                    } else {
                        toast(result.note || 'Google News check complete. No new mentions.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setChecking(false);
                }
            };

            const runBluesky = async () => {
                setCheckingBsky(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/bluesky/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult({ ...result, source: 'Bluesky' });
                    await reloadHistory();
                    if (result.new_incidents_created > 0) {
                        toast(`Bluesky: ${result.new_incidents_created} new mention(s) added`);
                        onIncidentsChanged?.();
                    } else {
                        toast(result.note || 'Bluesky check complete. No new mentions.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setCheckingBsky(false);
                }
            };

            const runAll = async () => {
                if (!confirm('Run all sources for this subject?\n\nGoogle News, Bluesky, Mastodon, X (Twitter), and sanctions screening.\n\nTakes ~60 seconds and may cost up to ~$2 in API fees (mostly X).')) return;
                setRunningAll(true);
                setError(null);
                setLastResult(null);
                setRunAllStatus('Starting…');
                try {
                    const handoff = await api(
                        `/api/v1/subjects/${subjectId}/run-all-sources`,
                        { method: 'POST' },
                        token
                    );
                    if (!handoff || !handoff.job_id) {
                        throw new Error('run-all did not return a job id');
                    }
                    toast('Run All started — polling for progress...');
                    const result = await pollJobStatus(handoff.job_id, (msg) => {
                        setRunAllStatus(msg);
                    });
                    await reloadHistory();
                    const parts = (result.sources || []).map(s => {
                        if (!s.ran) return `${s.source}: skipped`;
                        if (!s.succeeded) return `${s.source}: FAILED`;
                        return `${s.source}: ${s.new_incidents} new`;
                    });
                    const headline = `Run All complete · ${result.total_new_incidents} new incidents · ${result.sources_succeeded}/${result.sources_attempted} sources`;
                    const cost = result.total_estimated_cost_usd
                        ? ` · $${result.total_estimated_cost_usd.toFixed(2)}`
                        : '';
                    toast(`${headline}${cost}\n${parts.join(' · ')}`);
                    if (result.prolific_findings && result.prolific_findings.length) {
                        for (const finding of result.prolific_findings.slice(0, 3)) {
                            toast(`Prolific: ${finding}`);
                        }
                    }
                    if (result.total_new_incidents > 0) onIncidentsChanged?.();
                } catch (err) {
                    setError(err.message);
                    toast(`Run All error: ${err.message}`, 'error');
                } finally {
                    setRunningAll(false);
                    setRunAllStatus('');
                }
            };

            const runX = async () => {
                if (!confirm('Run X (Twitter) search now? Costs roughly $0.50 to $2 depending on query plan.')) return;
                setCheckingX(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/x/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult({ ...result, source: 'X' });
                    await reloadHistory();
                    const costStr = `$${result.estimated_cost_usd}`;
                    if (result.new_mentions_inserted > 0) {
                        toast(`X: ${result.new_mentions_inserted} new mention(s) added (${result.tweets_read} reads, ${costStr})`);
                        onIncidentsChanged?.();
                    } else {
                        toast(`X check complete. ${result.tweets_read} tweets read (${costStr}). No new mentions.`);
                    }
                    if (result.errors && result.errors.length) {
                        toast(`X errors: ${result.errors.slice(0, 2).join('; ')}`, 'error');
                    }
                } catch (err) {
                    setError(err.message);
                    toast(`X error: ${err.message}`, 'error');
                } finally {
                    setCheckingX(false);
                }
            };

            const [checkingOctolens, setCheckingOctolens] = useState(false);
            const runOctolens = async () => {
                setCheckingOctolens(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/octolens/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult({ ...result, source: 'Octolens' });
                    await reloadHistory();
                    if (result.new_mentions_inserted > 0) {
                        toast(`Octolens: ${result.new_mentions_inserted} new mention(s) added (${result.mentions_fetched} fetched)`);
                        onIncidentsChanged?.();
                    } else {
                        toast(`Octolens check complete. ${result.mentions_fetched} fetched. No new mentions.`);
                    }
                    if (result.errors && result.errors.length) {
                        toast(`Octolens errors: ${result.errors.slice(0, 2).join('; ')}`, 'error');
                    }
                } catch (err) {
                    setError(err.message);
                    toast(`Octolens error: ${err.message}`, 'error');
                } finally {
                    setCheckingOctolens(false);
                }
            };

            const [checkingMasto, setCheckingMasto] = useState(false);
            const runMastodon = async () => {
                setCheckingMasto(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/mastodon/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult({ ...result, source: 'Mastodon' });
                    await reloadHistory();
                    if (result.new_incidents_created > 0) {
                        toast(`Mastodon: ${result.new_incidents_created} new mention(s) added`);
                        onIncidentsChanged?.();
                    } else {
                        toast(result.note || 'Mastodon check complete. No new mentions.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setCheckingMasto(false);
                }
            };

            // Poll a background job until it terminates. Updates the
            // setProgressMessage callback with the current progress message.
            // Returns the job's `result` on success, throws on failure or timeout.
            const pollJobStatus = async (jobId, setProgressMessage) => {
                const POLL_INTERVAL_MS = 2000;
                const MAX_POLLS = 180; // 6 minutes
                for (let i = 0; i < MAX_POLLS; i++) {
                    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
                    const job = await api(`/api/v1/jobs/${jobId}`, {}, token);
                    if (setProgressMessage) {
                        const pct = job.progress || 0;
                        const msg = job.progress_message || job.status;
                        setProgressMessage(`Reclassifying… ${pct}% — ${msg}`);
                    }
                    if (job.status === 'success') return job.result;
                    if (job.status === 'failed') {
                        throw new Error(job.error || 'Job failed');
                    }
                    if (job.status === 'cancelled') {
                        throw new Error('Job was cancelled');
                    }
                }
                throw new Error('Job timed out after 6 minutes');
            };

            const runReclassify = async () => {
                if (!confirm('Re-run the AI classifier on every existing mention for this subject? Costs about 1-2p per 25 mentions.')) return;
                setReclassifying(true);
                setError(null);
                setLastResult(null);
                try {
                    // 1. Kick off the async job. Returns 202 + {job_id, status, kind}.
                    const handoff = await api(
                        `/api/v1/subjects/${subjectId}/online-mentions/reclassify`,
                        { method: 'POST' },
                        token
                    );
                    if (!handoff || !handoff.job_id) {
                        throw new Error('Reclassify did not return a job id');
                    }

                    // 2. Poll until complete.
                    toast('Reclassify started — this can take a minute or two...');
                    const result = await pollJobStatus(handoff.job_id, (msg) => {
                        // Could surface this in the UI; for now we leave the
                        // button label simple ("Reclassifying…")
                    });

                    // 3. Same handling as before — result has identical shape.
                    await reloadHistory();
                    if (result.reclassified > 0) {
                        toast(`Reclassified ${result.reclassified} of ${result.examined} mentions. ${result.changed_severity} had their severity change.`);
                        onIncidentsChanged?.();
                    } else if (result.examined === 0) {
                        toast('No existing mention incidents to reclassify.');
                    } else if (result.failed > 0) {
                        toast(`Reclassify failed for ${result.failed} mentions — check API key and try again.`);
                    } else {
                        toast('No changes from reclassify.');
                    }
                    if (result.suggested_assessment_update && result.suggested_assessment_update.drift_significant) {
                        setLastResult({
                            type: 'assessment_drift',
                            suggestion: result.suggested_assessment_update,
                        });
                    }
                } catch (err) {
                    setError(err.message);
                    toast(`Reclassify error: ${err.message}`);
                } finally {
                    setReclassifying(false);
                }
            };

            const classLabel = (c) => ({
                threat: 'Threat',
                doxxing: 'Doxxing',
                impersonation: 'Impersonation',
                disinformation: 'Disinformation',
                news_mention: 'News mention',
                hostile_threat: 'Hostile (threat)',
                hostile_doxxing: 'Hostile (doxxing)',
                hostile_impersonation: 'Hostile (impersonation)',
                hostile_disinformation: 'Hostile (disinformation)',
                neutral_mention: 'Neutral mention',
                off_target: 'Off-target',
            })[c] || c;

            const classBand = (c) => ({
                threat: 'high',
                doxxing: 'high',
                impersonation: 'moderate',
                disinformation: 'moderate',
                news_mention: 'low',
                hostile_threat: 'high',
                hostile_doxxing: 'high',
                hostile_impersonation: 'moderate',
                hostile_disinformation: 'moderate',
                neutral_mention: 'low',
                off_target: 'low',
            })[c] || 'low';

            // Shared helpers so the header counts and the row badges agree.
            const isNewMention = (m) => {
                if (!m.first_seen_at) return false;
                return (Date.now() - new Date(m.first_seen_at).getTime()) / 3600000 <= 48;
            };
            const isHostileMention = (m) => {
                const v = m.incident_llm_classification;
                if (v) return v.startsWith('hostile_');
                return ['threat','doxxing','impersonation','disinformation'].includes(m.classification);
            };
            const isOffTargetMention = (m) =>
                m.incident_llm_classification === 'off_target' || m.incident_status === 'false_positive';
            // Recency window: the watch only shows recent activity. A mention is
            // dated by its own content (article/post published date) and only
            // falls back to when we first saw it. That way a 2017 article that a
            // collector surfaces today is still treated as old and dropped.
            const MENTION_WINDOW_DAYS = 30;
            const _windowCutoff = Date.now() - MENTION_WINDOW_DAYS * 24 * 60 * 60 * 1000;
            const mentionWhen = (m) => {
                const d = m.published_at || m.first_seen_at;
                const t = d ? new Date(d).getTime() : NaN;
                return isNaN(t) ? null : t;
            };
            const withinWindow = (m) => {
                const t = mentionWhen(m);
                return t === null ? true : t >= _windowCutoff;   // keep undated; drop clearly-old
            };
            const _histAll = history || [];
            const _hist = _histAll.filter(withinWindow);
            const staleHidden = _histAll.length - _hist.length;
            const hostileCount = _hist.filter(m => isHostileMention(m) && !isOffTargetMention(m)).length;
            const newCount = _hist.filter(isNewMention).length;

            return (
                                <div className="panel mb-6">
                    <div className="panel-header flex justify-between items-center">
                        <div>
                            <div className="font-display text-xl gold">Hostile Mention Watch<HelpTip text={PANEL_HELP.hostile_mention_watch} /></div>
                            <div className="text-xs text-muted font-mono mt-1">
                                Multi-source collector · Google News, Bluesky, Mastodon, X, GDELT, The Gazette, RSS feeds
                            </div>
                            <div className="text-xs text-muted mt-1"
                                 title={`Only mentions from the last ${MENTION_WINDOW_DAYS} days are shown, dated by when the article or post was published. Older items stay on file but are kept out of the live watch.`}>
                                Showing last {MENTION_WINDOW_DAYS} days
                                {staleHidden > 0 && <span> · {staleHidden} older hidden</span>}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                            <div className="flex gap-2 items-center">
                                <button
                                    className="btn-gold"
                                    onClick={runAll}
                                    disabled={runningAll || checking || checkingBsky || checkingMasto || checkingX || reclassifying}
                                    title="Run Google News, Bluesky, Mastodon, X and sanctions screening for this subject in one pass."
                                >
                                    {runningAll ? (runAllStatus || 'Running…') : 'Run All Sources'}
                                </button>
                                <button
                                    className="btn-ghost"
                                    onClick={() => setAdvancedOpen(o => !o)}
                                    disabled={runningAll}
                                >
                                    {advancedOpen ? '▴ Hide advanced' : '▾ Advanced'}
                                </button>
                            </div>
                            {advancedOpen && (
                                <div className="flex gap-2 flex-wrap justify-end">
                                    <button
                                        className="btn-ghost"
                                        onClick={runReclassify}
                                        disabled={reclassifying || checking || checkingBsky || checkingMasto || checkingX || runningAll}
                                        title="Re-run the AI classifier on existing mention incidents."
                                    >
                                        {reclassifying ? 'Reclassifying…' : 'Reclassify with AI'}
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={runMastodon}
                                        disabled={checkingMasto || checking || checkingBsky || reclassifying || checkingX || runningAll}
                                    >
                                        {checkingMasto ? 'Mastodon…' : 'Run Mastodon'}
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={runBluesky}
                                        disabled={checkingBsky || checking || reclassifying || checkingMasto || checkingX || runningAll}
                                    >
                                        {checkingBsky ? 'Bluesky…' : 'Run Bluesky'}
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={runX}
                                        disabled={checkingX || checking || checkingBsky || reclassifying || checkingMasto || runningAll}
                                        title="Search X recent posts for hostile mentions. Pay-per-use."
                                    >
                                        {checkingX ? 'X…' : 'Run X'}
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={runOctolens}
                                        disabled={checkingOctolens || checkingX || checking || checkingBsky || reclassifying || checkingMasto || runningAll}
                                        title="Pull multi-platform hostile mentions from Octolens for this subject."
                                    >
                                        {checkingOctolens ? 'Octolens…' : 'Run Octolens'}
                                    </button>
                                    <button
                                        className="btn-ghost"
                                        onClick={runCheck}
                                        disabled={checking || checkingBsky || reclassifying || checkingMasto || checkingX || runningAll}
                                    >
                                        {checking ? 'Google News…' : 'Run Google News'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <PanelHowTo id="hostile_mention_watch" />

                    <div className="p-5">
                        {error && <div className="text-[var(--red)] text-sm mb-3">{error}</div>}

                        {checking && (
                            <div className="text-xs text-muted font-mono mb-3">
                                Running 25-30 queries against Google News with 1.5s pacing between calls. This takes about a minute. Please wait.
                            </div>
                        )}

                        {lastResult && lastResult.type === 'assessment_drift' && lastResult.suggestion && (
                            <div className="mb-4 p-3" style={{background: 'rgba(216, 154, 74, 0.10)', border: '1px solid var(--amber)'}}>
                                <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{color: 'var(--amber)'}}>
                                    Assessment may be stale
                                </div>
                                <div className="text-sm mb-3">
                                    {lastResult.suggestion.rationale}
                                </div>
                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <div className="text-xs font-mono text-muted">CURRENT</div>
                                        <div className="text-sm">
                                            {lastResult.suggestion.current_risk_band
                                                ? `${lastResult.suggestion.current_risk_band.toUpperCase()} · ${Number(lastResult.suggestion.current_composite_score).toFixed(1)}`
                                                : 'No active assessment'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-mono text-muted">SUGGESTED</div>
                                        <div className="text-sm" style={{color: 'var(--amber)'}}>
                                            {lastResult.suggestion.suggested_risk_band.toUpperCase()} · {Number(lastResult.suggestion.suggested_composite_score).toFixed(1)}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-xs font-mono text-muted">
                                    Layer breakdown: threat {Number(lastResult.suggestion.threat_score).toFixed(1)} · vulnerability {Number(lastResult.suggestion.vulnerability_score).toFixed(1)} · impact {Number(lastResult.suggestion.impact_score).toFixed(1)} · disinformation {Number(lastResult.suggestion.disinformation_score).toFixed(1)}
                                </div>
                                <div className="text-xs text-muted mt-2">
                                    To apply, click "Run Assessment" or "Update Assessment" at the top of the dossier — the suggestion will appear pre-filled for your sign-off.
                                </div>
                            </div>
                        )}

                        {lastResult && lastResult.type !== 'assessment_drift' && (
                            <div className="mb-4 p-3" style={{background: 'rgba(201,169,110,0.05)', border: '1px solid rgba(201,169,110,0.15)'}}>
                                <div className="text-xs font-mono text-muted mb-2">LAST RUN</div>
                                <div className="text-sm mb-2">
                                    {lastResult.queries_run} queries · {lastResult.total_results_seen} unique URLs seen · {lastResult.new_incidents_created} new incidents · {lastResult.skipped_already_seen} already known
                                </div>
                                {lastResult.by_classification && lastResult.by_classification.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {lastResult.by_classification.map(b => (
                                            <span key={b.classification} className={`severity-pill band-${classBand(b.classification)}`}>
                                                {classLabel(b.classification)} · {b.new_incidents}/{b.total_seen}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {lastResult.note && (
                                    <div className="text-xs text-muted font-mono mt-2">{lastResult.note}</div>
                                )}
                            </div>
                        )}

                        <div className="flex justify-between items-center mb-2">
                            <div className="text-xs font-mono uppercase tracking-widest text-muted">
                                Mention History ({history?.length || 0})
                                {hostileCount > 0 && <span style={{color:'var(--gold)'}}> · {hostileCount} hostile</span>}
                                {newCount > 0 && <span style={{color:'var(--amber, #f59e0b)', fontWeight:700}}> · {newCount} new</span>}
                            </div>
                            {history && history.length > 0 && (
                                <button className="text-xs text-muted hover:text-[var(--gold)] uppercase tracking-widest" onClick={() => setExpanded(!expanded)}>
                                    {expanded ? 'Hide' : 'Show'}
                                </button>
                            )}
                        </div>

                        {history && history.length > 0 && expanded && (
                            <div className="flex gap-2 mb-3">
                                {[
                                    {k: 'hostile_first', label: 'Newest first'},
                                    {k: 'hostile_only',  label: 'Hostile only'},
                                    {k: 'all',           label: 'All'},
                                ].map(opt => (
                                    <button key={opt.k} type="button"
                                        onClick={() => { setFilter(opt.k); if (selectedAccount) onClearAccount?.(); }}
                                        className="text-xs px-3 py-1 uppercase tracking-widest"
                                        style={{
                                            background: filter === opt.k ? 'rgba(201,169,110,0.15)' : 'transparent',
                                            border: '1px solid ' + (filter === opt.k ? 'var(--gold)' : 'var(--veil)'),
                                            color: filter === opt.k ? 'var(--gold)' : 'var(--paper-muted)',
                                            cursor: 'pointer',
                                        }}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {!history ? (
                            <div className="text-muted text-sm">Loading…</div>
                        ) : history.length === 0 ? (
                            <div className="text-muted text-sm">No mentions recorded yet. Click "Run Mention Check" to start.</div>
                        ) : expanded ? (
                            (() => {
                                // Build the displayed list according to filter + sort.
                                // Hostile-detection: prefer LLM verdict if present, else
                                // fall back to keyword classification.
                                const isHostile = (m) => {
                                    const v = m.incident_llm_classification;
                                    if (v) return v.startsWith('hostile_');
                                    // fallback to keyword class
                                    return ['threat','doxxing','impersonation','disinformation'].includes(m.classification);
                                };
                                const isOffTarget = (m) =>
                                    m.incident_llm_classification === 'off_target' ||
                                    m.incident_status === 'false_positive';
                                const sevRank = (s) => ({critical:4, high:3, moderate:2, low:1}[s] || 0);

                                let rows = _hist.slice();
                                if (selectedAccount) {
                                    rows = rows.filter(m => m.source_account === selectedAccount);
                                }
                                if (filter === 'hostile_only') {
                                    rows = rows.filter(m => isHostile(m) && !isOffTarget(m));
                                } else if (filter === 'hostile_first') {
                                    // keep all but push off-target to the bottom
                                    rows = rows.slice();
                                }
                                // Sort: newest first. Off-target/false-positive
                                // mentions sink to the bottom so noise never tops
                                // the list, but everything else is strictly by
                                // recency (most recently seen first).
                                rows.sort((a, b) => {
                                    const aOff = isOffTarget(a);
                                    const bOff = isOffTarget(b);
                                    if (aOff !== bOff) return aOff ? 1 : -1;
                                    return new Date(b.first_seen_at) - new Date(a.first_seen_at);
                                });

                                if (rows.length === 0) {
                                    return (
                                        <div className="text-xs text-muted italic mt-2">
                                            No mentions match this filter.
                                            {filter === 'hostile_only' && _hist.length > 0 && (
                                                <span> ({_hist.length} neutral/off-target hidden.)</span>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <div className="space-y-2 mt-2">
                                        {selectedAccount && (
                                            <div style={{display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', marginBottom:'4px', background:'rgba(201,169,110,0.08)', border:'1px solid rgba(201,169,110,0.25)', fontSize:'12px'}}>
                                                <span style={{color:'var(--gold)'}}>Showing @{selectedAccount}</span>
                                                <button onClick={() => onClearAccount?.()} style={{fontSize:'11px', padding:'2px 8px', background:'transparent', color:'var(--text-muted)', border:'1px solid var(--rule)', cursor:'pointer'}}>clear</button>
                                            </div>
                                        )}
                                        {rows.map(m => {
                                            const hostile = isHostile(m) && !isOffTarget(m);
                                            const offTgt = isOffTarget(m);
                                            const isNew = isNewMention(m) && !offTgt;
                                            const llmCls = m.incident_llm_classification;
                                            return (
                                                <div key={m.id} className="py-2 border-b border-veil last:border-b-0"
                                                    style={{
                                                        ...(offTgt ? {opacity: 0.5} : {}),
                                                        ...(isNew ? {borderLeft: '3px solid var(--amber, #f59e0b)', paddingLeft: '8px'} : {}),
                                                    }}>
                                                    <div className="flex justify-between items-start gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <a href={/^https?:\/\//i.test(String(m.url||'').trim()) ? m.url : undefined} target="_blank" rel="noopener noreferrer"
                                                                onClick={(e) => { const u = String(m.url||'').trim(); if (/^https?:\/\//i.test(u)) { e.preventDefault(); window.open(u, '_blank', 'noopener,noreferrer'); } else { e.preventDefault(); } }}
                                                                className="text-sm hover:text-[var(--gold)] block truncate"
                                                                style={hostile ? {color: 'var(--gold)', fontWeight: 600} : {}}>
                                                                {isNew && <span style={{background:'var(--amber, #f59e0b)', color:'var(--bg)', fontSize:'9px', fontWeight:700, letterSpacing:'0.1em', padding:'1px 5px', borderRadius:'3px', marginRight:'6px'}}>NEW</span>}
                                                                {m.title || m.url}
                                                            </a>
                                                            {m.incident_llm_reasoning && (
                                                                <div className="text-xs text-muted italic mt-1 leading-snug">
                                                                    {m.incident_llm_reasoning}
                                                                </div>
                                                            )}
                                                            {m.snippet && !m.incident_llm_reasoning && (
                                                                <div className="text-xs text-muted mt-1 line-clamp-2">{m.snippet}</div>
                                                            )}
                                                            <div className="text-xs text-dim font-mono mt-1">
                                                                {m.source} · {m.query_label || ''} · first seen {new Date(m.first_seen_at).toLocaleString('en-GB')}
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            <span className={`severity-pill band-${classBand(llmCls || m.classification)}`}>
                                                                {classLabel(llmCls || m.classification)}
                                                            </span>
                                                            {m.incident_severity && (
                                                                <span className={`label-tag sev-${m.incident_severity}`} style={{fontSize:'10px'}}>
                                                                    {m.incident_severity}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="text-xs text-muted">Click Show to expand the list.</div>
                        )}

                        <div className="text-xs text-dim font-mono mt-4">
                            Mentions are matched on subject name, aliases, and online handles crossed with hostile keyword sets (threat, doxxing, impersonation, disinformation). Each new URL becomes a draft incident classified by which keyword set matched, for analyst sign-off.
                        </div>
                    </div>
                </div>
            );
        }

export { HostileMentionPanel };
